import {
	Component,
	OnInit,
	Input,
	OnDestroy,
	OnChanges,
	SimpleChanges,
	Output,
	EventEmitter,
	ElementRef,
	ViewChild,
	AfterContentInit,
	ViewEncapsulation,
	AfterViewInit
} from '@angular/core';
import { IdService } from '../../shared/services/id.service';
import { BehaviorSubject, Subscription } from 'rxjs';
import { skip } from 'rxjs/operators';
import { baseTransitionAnimations, getCssFriendlyHeight } from './animations';
import anime from 'animejs';
import { TTransitionTypes, ITransitionEvent } from './transition.types';

@Component({
	selector: 'rbc-transition',
	templateUrl: './transition.component.html',
	styleUrls: ['./transition.component.scss'],
	encapsulation: ViewEncapsulation.None
})
export class TransitionComponent implements OnInit, OnDestroy, OnChanges, AfterContentInit, AfterViewInit {
	nspace = 'rbc-transition';
	/* props */
	// NOTE: for opacity max-height and max-height, the max-height has to be set on the CONTENT projected
	// within this component directly
	@Input() id: string;
	@Input() type: TTransitionTypes = 'opacity';
	@Input() group: boolean = false;
	@Input() state: boolean = false; // NOTE: flipping b/w T&F also flips animation state, and is required for transition to apply
	@Input() duration: number;
	@Input() forceState: boolean;
	@Input() isAbsolute: boolean = false;
	@Input() bindToContent: boolean = false;
	@Input() softHide: boolean = false;
	@Output() onTransition = new EventEmitter<ITransitionEvent>();

	/* private */
	hidden: boolean;
	trueHeight: number = 0;
	trueMaxHeight: string;
	entryAnimRef;
	exitAnimRef;
	$animationState: BehaviorSubject<boolean>;
	$animationStateSub: Subscription;
	@ViewChild('transitionContent') transitionContent: ElementRef;

	constructor(private idService: IdService, private hostRef: ElementRef) {
		(this.hostRef.nativeElement as HTMLElement).classList.add(this.nspace);
	}

	ngOnInit() {
		this.id = this.id ? this.id : this.idService.generate();
		this.duration = typeof this.duration === 'number' ? this.duration : this.getDefaultDuration(this.type);
		this.$animationState = new BehaviorSubject<boolean>(this.state);
	}

	ngAfterContentInit(): void {
		if (this.hasContent()) {
			const target = this.getTargetElement();
			// not a comment node?
			if (target && target.nodeType !== 8) {
				// fetch height & max height quickly before children are hidden
				this.setTrueHeight(target);
				this.setTrueMaxHeight(target);
				this.toggleTargetVisibility(target);

				// special cases
				if (this.isAbsolute) {
					this.enableAbsoluteBox();
				}
			}
		}
	}

	ngAfterViewInit(): void {
		// not a comment node?
		if (this.hasContent() && this.getTargetNode().nodeType !== 8) {
			this.loadAnimation(this.type);
			this.mountAnimationStateListener();
			this.bounceBackState(this.duration);
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes) {
			// load animation first
			if (changes.type && !changes.type.isFirstChange()) {
				this.loadAnimation(changes.type.currentValue);
			}
			// before changing state
			if (changes.state && !changes.state.isFirstChange()) {
				this.$animationState.next(!this.$animationState.getValue());
				this.bounceBackState(this.duration);
			}
		}
	}

	ngOnDestroy(): void {
		this.idService.remove(this.id);
		this.removeAnimations();
		if (this.$animationStateSub) {
			this.$animationStateSub.unsubscribe();
		}
	}

	getDefaultDuration(type: TTransitionTypes): number {
		switch (type) {
			case 'height':
			// fall through
			case 'max-height':
				return 500;
			default:
				// everything else
				return 250;
		}
	}

	// positions the transition container absolutely, stretched
	enableAbsoluteBox() {
		this.getTargetElement().classList.add('position-absolute');
	}

	// NOTE: make sure to set the delay to the duration of the transition AFTER which the state needs to bounce back
	bounceBackState(delay: number) {
		if (this.forceState != null && this.$animationState.value !== this.forceState) {
			setTimeout(() => {
				this.$animationState.next(this.forceState);
			}, delay);
		}
	}

	// if no content child exists, then the transition will not take place
	hasContent(): boolean {
		return !!this.transitionContent && !!(this.transitionContent.nativeElement as HTMLElement).childElementCount;
	}

	// the element to apply transitions to
	getTargetElement(): HTMLElement {
		return this.bindToContent ? this.getContentElement() : this.getContentContainerElement();
	}
	getContentContainerElement() {
		return this.hasContent() ? ((this.transitionContent.nativeElement as HTMLElement) as HTMLElement) : null;
	}
	// the first child of the element that transitions are applied to
	getContentElement() {
		return this.hasContent
			? ((this.transitionContent.nativeElement as HTMLElement).firstElementChild as HTMLElement)
			: null;
	}

	// the node to apply transitions to
	getTargetNode(): Node {
		return this.bindToContent ? this.getContentNode() : this.getContentContainerNode();
	}
	getContentContainerNode() {
		return this.hasContent() ? (this.transitionContent.nativeElement as HTMLElement) : null;
	}
	// the first child of the node that transitions are applied to
	getContentNode() {
		return this.hasContent() ? (this.transitionContent.nativeElement as HTMLElement).firstChild : null;
	}

	setTrueHeight(target: HTMLElement) {
		if (target) {
			this.trueHeight = target.offsetHeight;
		}
	}

	setTrueMaxHeight(target: HTMLElement) {
		if (target) {
			this.trueMaxHeight = target.style.maxHeight;
		}
	}

	toggleTargetVisibility(target: HTMLElement) {
		if (!this.$animationState.getValue()) {
			this.hideTarget(target);
		} else {
			this.showTarget(target);
		}
	}

	hideTarget(target: HTMLElement) {
		this.hidden = true;
		if (this.softHide) {
			target.classList.add('hidden-soft');
		} else {
			target.classList.add('hidden');
		}
	}

	showTarget(target: HTMLElement) {
		this.hidden = false;
		if (this.softHide) {
			target.classList.remove('hidden-soft');
		} else {
			target.classList.remove('hidden');
		}
	}

	// state controls animations to play
	mountAnimationStateListener() {
		this.$animationStateSub = this.$animationState.pipe(skip(1)).subscribe(state => {
			this.resetAnimations(); // unload any changed styles
			if (state) {
				// false -> true
				this.animateEntry();
			} else {
				// true -> false
				this.animateExit();
			}
		});
	}

	isHeight() {
		return this.type === 'opacity-height' || this.type === 'height';
	}

	isMaxHeight() {
		return this.type === 'max-height' || this.type === 'opacity-max-height';
	}

	loadAnimation(type: string) {
		const target = this.getTargetNode();

		if (target && baseTransitionAnimations[type] && !!baseTransitionAnimations[type].length) {
			this.resetAnimations(); // unload any changed styles to prep for new anim

			this.entryAnimRef = baseTransitionAnimations[type][0]({
				targets: target,
				duration: this.duration,
				onBegin: this.entryAnimationOnBegin.bind(this),
				onComplete: this.entryAnimationOnComplete.bind(this),
				height: this.isHeight()
					? getCssFriendlyHeight(this.trueHeight)
					: this.isMaxHeight()
					? this.trueMaxHeight
					: null
			});
			this.exitAnimRef = baseTransitionAnimations[type][1]({
				targets: target,
				duration: this.duration,
				onBegin: this.exitAnimationOnBegin.bind(this),
				onComplete: this.exitAnimationOnComplete.bind(this),
				height: this.isHeight()
					? getCssFriendlyHeight(this.trueHeight)
					: this.isMaxHeight()
					? this.trueMaxHeight
					: null
			});
		} else {
			// reset
			this.entryAnimRef = null;
			this.exitAnimRef = null;
		}
	}

	/* utility */
	removeAnimations(targetElement?: Node) {
		if (targetElement) {
			anime.remove(targetElement);
		} else {
			anime.remove(this.getTargetNode());
		}
	}

	resetAnimations(targetElement?: Node) {
		if (targetElement) {
			(targetElement as HTMLElement).setAttribute('style', null);
		} else if (this.hasContent()) {
			this.getTargetElement().setAttribute('style', null);
		}
	}

	entryAnimationOnBegin(anim) {
		if (anim.animatables && anim.animatables[0]) {
			const target = anim.animatables[0].target as HTMLElement;

			this.showTarget(target);
			if (this.isHeight() || this.isMaxHeight()) {
				target.classList.add('hide-overflow');
			}

			this.onTransition.emit({
				type: 'enter',
				element: target
			});
		}
	}

	entryAnimationOnComplete(anim) {
		if (anim.animatables && anim.animatables[0]) {
			const target = anim.animatables[0].target as HTMLElement;

			if (this.isHeight() || this.isMaxHeight()) {
				target.classList.remove('hide-overflow');
			}

			this.onTransition.emit({
				type: 'enter-complete',
				element: target
			});
		}
	}

	exitAnimationOnBegin(anim) {
		if (anim.animatables && anim.animatables[0]) {
			const target = anim.animatables[0].target as HTMLElement;

			if (this.isHeight() || this.isMaxHeight()) {
				target.classList.add('hide-overflow');
			}

			this.onTransition.emit({
				type: 'leave',
				element: target
			});
		}
	}

	exitAnimationOnComplete(anim) {
		if (anim.animatables && anim.animatables[0]) {
			const target = anim.animatables[0].target as HTMLElement;

			this.hideTarget(target);
			if (this.isHeight() || this.isMaxHeight()) {
				target.classList.remove('hide-overflow');
				target.style.height = 'auto';
			}

			this.onTransition.emit({
				type: 'leave-complete',
				element: target
			});
		}
	}

	animateEntry() {
		if (this.entryAnimRef) {
			this.entryAnimRef.play();
		}
	}

	animateExit() {
		if (this.exitAnimRef) {
			this.exitAnimRef.play();
		}
	}
}
